import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { productNeedsBackgroundSelection } from '../../../productConfigPredicates';
import CategoryPicker from '../../../../shared/CategoryPicker';
import useFoldOnSelect from '../../../../hooks/useFoldOnSelect';
import useIsPhone from '../../../../hooks/useIsPhone';
import BackgroundOption from './BackgroundOption';
import type { Background } from './types';

const BackgroundOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 14px;

    & > div {
      flex: 0 0 calc(33.33% - 10px);
      max-width: calc(33.33% - 10px);
    }
  }
`;

interface BackgroundCategory {
  id: number | string;
  name: string;
  background_images?: Background[];
}

export interface BackgroundsConfigProps {
  onZoomClick: (e: React.MouseEvent, backgroundId: number) => void;
}

const BackgroundsConfig = ({ onZoomClick }: BackgroundsConfigProps) => {
  const activeConfigSlotIndex = useSelector(
    (state: any) => state.productConfig.activeConfigSlotIndex,
  );
  const activeConfigStep = useSelector(
    (state: any) => state.productConfig.activeConfigStep,
  );
  const backgroundOptions = useSelector(
    (state: any) => state.shoot.backgroundOptions,
  );
  const product = useSelector(
    (state: any) => state.productConfig.currentProduct,
  );

  const [backgroundsCategoryIndex, setBackgroundsCategoryIndex] = useState(0);

  const isPhone = useIsPhone();

  // One set is not a choice: no tab worth pressing, nothing to collapse, and
  // no picker once collapsed.
  const hasCategoryChoice = backgroundOptions.length > 1;

  // The lone-set tab only goes away on phones, where every row costs the
  // thumbnails viewport height. Desktop keeps rendering it exactly as it
  // always has (ENG-2551 promises >=768px is pixel-identical).
  const showTabs = hasCategoryChoice || !isPhone;

  // Phones land folded: the picker opens collapsed on the first set, and a
  // selection re-folds and scrolls that set's first row into view.
  const { folded, fold, unfold, reset, gridRef } = useFoldOnSelect({
    foldByDefault: hasCategoryChoice,
  });

  const selectCategory = (index: number) => {
    setBackgroundsCategoryIndex(index);
    if (hasCategoryChoice) fold();
  };

  useEffect(() => {
    if (activeConfigStep === 'background') {
      setBackgroundsCategoryIndex(0);
    }
  }, [activeConfigSlotIndex, activeConfigStep]);

  // This component never unmounts when the step moves on — the parent only
  // hides it — so the hook's unmount cleanup cannot release the chrome. Each
  // slot/step change does it here: back on the background step, return to the
  // viewport's default (folded on phones); on any other step, release the
  // chrome so the tracker is never left slim over the pose picker.
  useEffect(() => {
    if (activeConfigStep === 'background') {
      reset();
    } else {
      unfold();
    }
  }, [activeConfigSlotIndex, activeConfigStep, reset, unfold]);

  if (!product) return null;

  // Unreachable via the current seeding paths (they all gate the 'background'
  // step on this same predicate) — kept as the last-line guard at the
  // rendering boundary, so a stray 'background' step can never show a
  // selectable grid for a product with no background slots or an
  // auto-resolved background, where a tap would store a stray, possibly
  // paid, selection (ENG-2300).
  if (!productNeedsBackgroundSelection(product)) {
    return null;
  }

  // Backgrounds only render for an explicitly seeded 'background' step. Every
  // open path (user slot taps, pickNextConfigSlot advancement, the desktop
  // auto-open, and the incentive flow) goes through openConfigSlot, which
  // seeds slotIndex + step and opens the panel atomically, so a 'background'
  // step always comes with an active slot.
  if (activeConfigStep !== 'background') {
    return null;
  }

  return (
    <div
      className="bnl-product-config"
      ref={gridRef}
      style={{ scrollMarginTop: 'var(--chrome-offset, 52px)' }}
    >
      {folded ? (
        <CategoryPicker
          categories={backgroundOptions}
          selectedIndex={backgroundsCategoryIndex}
          onSelect={(_category, index: number) => selectCategory(index)}
          onExpand={unfold}
        />
      ) : !showTabs ? null : (
        <ul className="bnl-product-config__bg-selector__tabs-container">
          {backgroundOptions.map(
            (category: BackgroundCategory, index: number) => (
              <li
                key={category.id}
                className={`bnl-product-config__bg-selector__tab ${
                  index === backgroundsCategoryIndex ? 'group-active' : ''
                }`}
                onClick={() => selectCategory(index)}
              >
                {category.name}
              </li>
            ),
          )}
        </ul>
      )}
      <BackgroundOptionsGrid className="equal-height">
        {backgroundOptions[backgroundsCategoryIndex]?.background_images?.map(
          (background: any) => (
            <BackgroundOption
              key={background.id}
              background={background}
              onZoomClick={onZoomClick}
            />
          ),
        )}
      </BackgroundOptionsGrid>
    </div>
  );
};

export default BackgroundsConfig;
